import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

interface RadioGroupProps {
    options: { value: string; label: string }[];
    value: string | null;
    onChange: (value: string) => void;
    size?: 'default' | 'small';
    orientation?: 'horizontal' | 'vertical';
    disabled?: boolean;
}

export function RadioGroup({
    options,
    value,
    onChange,
    size = 'default',
    orientation = 'horizontal',
    disabled = false,
}: RadioGroupProps) {
    const small = size === 'small';

    return orientation === 'horizontal' ? (
        <Row>
            {options.map((opt) => {
                const checked = value === opt.value;
                return (
                    <Option key={opt.value} onClick={() => !disabled && onChange(opt.value)} disabled={disabled}>
                        <Circle checked={checked} small={small}>
                            {checked && <Dot small={small} />}
                        </Circle>
                        <LabelText small={small}>{opt.label}</LabelText>
                    </Option>
                );
            })}
        </Row>
    ) : (
        <Column>
            {options.map((opt) => {
                const checked = value === opt.value;
                return (
                    <Option key={opt.value} onClick={() => !disabled && onChange(opt.value)} disabled={disabled}>
                        <Circle checked={checked} small={small}>
                            {checked && <Dot small={small} />}
                        </Circle>
                        <LabelText small={small}>{opt.label}</LabelText>
                    </Option>
                );
            })}
        </Column>
    );
}

const Row = styled('div')({
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
});

const Column = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

const Option = styled('div')<{ disabled?: boolean }>(({ disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
}));

const Circle = styled('div')<{ checked: boolean; small: boolean }>(({ checked, small }) => ({
    width: small ? 15 : 18,
    height: small ? 15 : 18,
    borderRadius: '50%',
    border: `2px solid ${checked ? Colors.primary : Colors.border}`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const Dot = styled('div')<{ small: boolean }>(({ small }) => ({
    width: small ? 7 : 8,
    height: small ? 7 : 8,
    borderRadius: '50%',
    backgroundColor: Colors.primary,
}));

const LabelText = styled('span')<{ small: boolean }>(({ small }) => ({
    fontSize: small ? 13 : 14,
    fontWeight: 500,
    lineHeight: small ? '16px' : '18px',
    color: Colors.primary,
}));
